import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowRightLeft, BadgeCheck, CalendarClock, Camera, ChevronDown, ChevronUp, Copy, Crown, ImageIcon, Languages, LoaderCircle, ScanText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SpeakButton } from "@/components/dashboard/SpeakButton";
import { EASE } from "@/components/landing/Reveal";
import { TARGETS } from "@/lib/langs";

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MD = { p: (p) => <p className="mb-2 last:mb-0" {...p} /> };

function quotaToast(err) {
  if (err.response?.status === 402) {
    toast.error(err.response.data.detail, {
      action: { label: "Passer à Premium", onClick: () => (window.location.href = "/dashboard/parametres") },
    });
    return true;
  }
  return false;
}

function langLabel(code) {
  return TARGETS.find((t) => t.code === code)?.label || code;
}

function DetectedNotices({ detected }) {
  if (!detected) return null;
  const fr = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const items = [];
  if (detected.deadline) items.push({
    icon: CalendarClock, testid: "detected-deadline",
    text: detected.deadline.created
      ? `Échéance ajoutée à votre agenda : « ${detected.deadline.title} » — ${fr(detected.deadline.date)}.`
      : `Date limite repérée : « ${detected.deadline.title} » — ${fr(detected.deadline.date)} (déjà dans vos échéances).`,
  });
  if (detected.titre_sejour) items.push({
    icon: BadgeCheck, testid: "detected-titre",
    text: `Titre de séjour enregistré dans votre profil : ${detected.titre_sejour.type}${detected.titre_sejour.expiry_date ? ` (valable jusqu'au ${fr(detected.titre_sejour.expiry_date)})` : ""}.`,
  });
  if (detected.renewal_deadline) items.push({
    icon: CalendarClock, testid: "detected-renewal",
    text: `Rappel de renouvellement ajouté à vos échéances pour le ${fr(detected.renewal_deadline.date)}.`,
  });
  if (!items.length) return null;
  return (
    <div className="mt-3 space-y-2 border-t border-bleu/10 pt-3" dir="ltr" data-testid="interprete-detected-notices">
      {items.map((it) => (
        <p key={it.testid} className="flex items-start gap-2 text-xs font-semibold text-emerald-700" data-testid={it.testid}>
          <it.icon className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span>{it.text}</span>
        </p>
      ))}
    </div>
  );
}

function HistoryItem({ doc, onDelete }) {
  const [open, setOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !imgUrl) {
      try {
        const res = await api.get(`/interpreter/documents/${doc.id}/image`, { responseType: "blob" });
        setImgUrl(URL.createObjectURL(res.data));
      } catch {
        toast.error("Impossible de charger l'image du document.");
      }
    }
  };

  useEffect(() => () => { if (imgUrl) URL.revokeObjectURL(imgUrl); }, [imgUrl]);

  return (
    <div className="card-integra overflow-hidden" data-testid="interprete-history-item">
      <button onClick={toggle} className="flex w-full items-center gap-3 p-4 text-left" data-testid="interprete-history-toggle">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light text-bleu"><ScanText className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-slate-900">{doc.filename || "Document"}</p>
          <p className="text-[11px] text-slate-400">
            {new Date(doc.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })} · Traduit vers {langLabel(doc.target)}
          </p>
        </div>
        {open ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4" data-testid="interprete-history-detail">
          <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
            <div className="flex items-start justify-center rounded-xl border border-slate-100 bg-slate-50 p-2">
              {imgUrl
                ? <img src={imgUrl} alt={doc.filename || "Document scanné"} className="max-h-48 rounded-lg object-contain" data-testid="interprete-history-image" />
                : <LoaderCircle className="my-8 h-5 w-5 animate-spin text-slate-300" />}
            </div>
            <div dir={doc.target === "ar" ? "rtl" : "ltr"} className="text-sm leading-relaxed text-slate-800">
              <ReactMarkdown components={MD}>{doc.translation}</ReactMarkdown>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <SpeakButton text={doc.translation} />
            <button onClick={() => onDelete(doc.id)}
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-[11px] font-semibold text-slate-500 transition-colors hover:border-rouge/40 hover:text-rouge"
              data-testid="interprete-history-delete">
              <Trash2 className="h-3 w-3" /> Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Interprete() {
  const { user, setUser } = useAuth();
  const [text, setText] = useState("");
  const [target, setTarget] = useState(user?.preferred_lang || "fr");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [docTarget, setDocTarget] = useState(user?.preferred_lang || "fr");
  const [docLoading, setDocLoading] = useState(false);
  const [docResult, setDocResult] = useState(null);
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/interpreter/documents").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const translate = async (e) => {
    e.preventDefault();
    if (text.trim().length < 2) return;
    setLoading(true);
    try {
      const { data } = await api.post("/interpreter", { text: text.trim(), target });
      setResult(data);
    } catch (err) {
      if (!quotaToast(err)) toast.error("Impossible de traduire. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result.translation);
    toast.success("Traduction copiée.");
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Format non pris en charge. Utilisez une photo JPEG, PNG ou WebP.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image trop volumineuse (8 Mo maximum).");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setDocResult(null);
    setPreview(URL.createObjectURL(f));
  };

  const analyzeDoc = async () => {
    if (!file || docLoading) return;
    setDocLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("target", docTarget);
      const { data } = await api.post("/interpreter/document", form);
      setDocResult(data);
      setHistory((h) => [data, ...h]);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      if (data.detected?.titre_sejour) api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
      toast.success("Document lu et traduit.");
    } catch (err) {
      if (!quotaToast(err)) toast.error(err.response?.data?.detail || "Impossible d'analyser le document. Veuillez réessayer.");
    } finally {
      setDocLoading(false);
    }
  };

  const deleteDoc = async (id) => {
    try {
      await api.delete(`/interpreter/documents/${id}`);
      setHistory((h) => h.filter((d) => d.id !== id));
      toast.success("Document supprimé.");
    } catch {
      toast.error("Impossible de supprimer le document.");
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="page-interprete">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Interprète IA</h1>
        <p className="mt-1 text-sm text-slate-500">
          Traduisez un texte ou la photo d'un document administratif dans la langue de votre choix.
          Les termes administratifs français sont expliqués simplement.
          {user?.plan !== "premium" && " (10 traductions/jour en compte gratuit)"}
        </p>
      </div>

      <motion.form
        onSubmit={translate}
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}
        className="card-integra p-6" data-testid="interprete-form"
      >
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><Languages className="h-4 w-4 text-bleu" /> Traduire un texte</h2>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          maxLength={6000}
          placeholder="Collez ici le texte à traduire (dans n'importe quelle langue)… Ex : un courrier de la préfecture, un email de la CAF, ou votre question à traduire en français."
          className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
          data-testid="interprete-input"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <ArrowRightLeft className="h-3.5 w-3.5" /> Traduire vers :
          </label>
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-700 focus:border-bleu focus:outline-none"
            data-testid="interprete-target-select">
            {TARGETS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <button type="submit" disabled={loading || text.trim().length < 2}
            className="btn-primary ml-auto !px-6 !py-2.5 disabled:opacity-50" data-testid="interprete-submit">
            {loading ? <><LoaderCircle className="h-4 w-4 animate-spin" /> Traduction…</> : <><Languages className="h-4 w-4" /> Traduire</>}
          </button>
        </div>
      </motion.form>

      {result && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
          className="card-integra p-6" data-testid="interprete-result" dir={result.target === "ar" ? "rtl" : "ltr"}>
          <div className="flex items-center justify-between gap-3" dir="ltr">
            <p className="text-xs font-bold uppercase tracking-wider text-bleu">Traduction — {langLabel(result.target)}</p>
            <div className="flex items-center gap-2">
              <SpeakButton text={result.translation} />
              <button onClick={copy} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3.5 py-1.5 text-[11px] font-semibold text-slate-600 hover:border-bleu/40 hover:text-bleu" data-testid="interprete-copy">
                <Copy className="h-3 w-3" /> Copier
              </button>
            </div>
          </div>
          <div className="mt-3 text-sm leading-relaxed text-slate-800">
            <ReactMarkdown components={MD}>{result.translation}</ReactMarkdown>
          </div>
        </motion.div>
      )}

      <motion.section
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08, ease: EASE }}
        className="card-integra p-6" data-testid="interprete-document-section"
      >
        <h2 className="flex items-center gap-2 text-sm font-bold text-slate-900"><Camera className="h-4 w-4 text-bleu" /> Photo de document</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
          Prenez en photo un courrier, un formulaire ou un avis administratif : l'IA lit le document,
          le traduit dans votre langue et vous explique ce que vous devez faire.
        </p>

        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => pickFile(e.target.files?.[0])} data-testid="interprete-file-input" />

        {preview && (file || docResult) ? (
          <div className="mt-4 flex items-start gap-4">
            <img src={preview} alt="Aperçu du document" className="max-h-44 rounded-xl border border-slate-100 object-contain" data-testid="interprete-doc-preview" />
            <button onClick={() => fileRef.current?.click()} className="rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-500 hover:border-bleu/40 hover:text-bleu" data-testid="interprete-doc-change">
              Changer de photo
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()}
            className="mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 py-8 text-slate-400 transition-colors hover:border-bleu/40 hover:text-bleu"
            data-testid="interprete-doc-dropzone">
            <ImageIcon className="h-7 w-7" />
            <span className="text-xs font-semibold">Choisir une photo (JPEG, PNG ou WebP — 8 Mo max)</span>
          </button>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <ArrowRightLeft className="h-3.5 w-3.5" /> Traduire vers :
          </label>
          <select value={docTarget} onChange={(e) => setDocTarget(e.target.value)}
            className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-700 focus:border-bleu focus:outline-none"
            data-testid="interprete-doc-target-select">
            {TARGETS.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
          <button onClick={analyzeDoc} disabled={docLoading || !file}
            className="btn-primary ml-auto !px-6 !py-2.5 disabled:opacity-50" data-testid="interprete-doc-submit">
            {docLoading ? <><LoaderCircle className="h-4 w-4 animate-spin" /> Lecture du document…</> : <><ScanText className="h-4 w-4" /> Lire et traduire</>}
          </button>
        </div>

        {docResult && (
          <div className="mt-4 rounded-xl border border-bleu/15 bg-bleu-light/50 p-4" dir={docResult.target === "ar" ? "rtl" : "ltr"} data-testid="interprete-doc-result">
            <div className="flex items-center justify-between gap-3" dir="ltr">
              <p className="text-xs font-bold uppercase tracking-wider text-bleu">Document traduit — {langLabel(docResult.target)}</p>
              <SpeakButton text={docResult.translation} />
            </div>
            <div className="mt-2 text-sm leading-relaxed text-slate-800">
              <ReactMarkdown components={MD}>{docResult.translation}</ReactMarkdown>
            </div>
            <DetectedNotices detected={docResult.detected} />
          </div>
        )}
      </motion.section>

      {history.length > 0 && (
        <section data-testid="interprete-history-section">
          <h2 className="mb-3 text-base font-bold text-slate-900">Mes documents scannés</h2>
          <div className="space-y-3">
            {history.map((d) => <HistoryItem key={d.id} doc={d} onDelete={deleteDoc} />)}
          </div>
        </section>
      )}

      {user?.plan !== "premium" && (
        <p className="flex items-center justify-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-bold text-amber-700" data-testid="interprete-premium-note">
          <Crown className="h-4 w-4" /> Traductions illimitées avec Premium — 7,99 €/mois
        </p>
      )}
    </div>
  );
}
